import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { Heart, Shield, Users, Sparkles, ArrowRight } from 'lucide-react';

const HomePageDetailed: React.FC = () => {
  const { isConnected, connectWallet, isLoading } = useWallet();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-legacy-warm-white via-white to-legacy-off-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-display font-bold text-primary-900 mb-6">
                Preserve Your{' '}
                <span className="text-accent-wood">Legacy</span>
                <br />
                <span className="text-primary-700">Forever</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-primary-600 mb-8 max-w-3xl mx-auto">
                Tokenize and document the things you love and value. Build provenance through their journey 
                and share precious memories with your family.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                {!isConnected ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={connectWallet}
                    disabled={isLoading}
                    className="bg-accent-wood hover:bg-accent-wood-light text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center gap-2 transition-colors"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Shield className="w-5 h-5" />
                        Connect Wallet
                      </>
                    )}
                  </motion.button>
                ) : (
                  <Link
                    to="/dashboard"
                    className="bg-accent-wood hover:bg-accent-wood-light text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center gap-2 transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                    Go to Dashboard
                  </Link>
                )}
                
                <Link
                  to="/mint"
                  className="border-2 border-accent-wood text-accent-wood hover:bg-accent-wood hover:text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center gap-2 transition-colors"
                >
                  <Sparkles className="w-5 h-5" />
                  Start Minting
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-primary-900 mb-4">
              Why Choose <span className="text-accent-wood">LegacyLayer</span>?
            </h2>
            <p className="text-xl text-primary-600 max-w-2xl mx-auto">
              We make it easy for families to preserve their most precious memories and valuable items 
              on the blockchain with unmatched security and simplicity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center p-8 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200"
            >
              <div className="w-16 h-16 bg-accent-wood rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-primary-900 mb-4">Family-Focused</h3>
              <p className="text-primary-600">
                Share your precious memories and valuable items with family members. 
                Create a digital legacy that can be passed down through generations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center p-8 rounded-2xl bg-gradient-to-br from-accent-wood/10 to-accent-wood/20 border border-accent-wood/30"
            >
              <div className="w-16 h-16 bg-accent-wood rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-primary-900 mb-4">Secure & Proven</h3>
              <p className="text-primary-600">
                Multi-chain support ensures your items are stored securely on Polygon, XRPL, and Solana. 
                Complete provenance tracking for every transaction.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center p-8 rounded-2xl bg-gradient-to-br from-primary-200/50 to-primary-300/50 border border-primary-300"
            >
              <div className="w-16 h-16 bg-primary-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-primary-900 mb-4">Easy to Use</h3>
              <p className="text-primary-600">
                Intuitive interface designed for everyone. No technical knowledge required. 
                Simply upload, describe, and mint your precious items with ease.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary-900 to-primary-800">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-display font-bold text-white mb-6">
            Ready to <span className="text-accent-wood-pale">Preserve</span> Your Legacy?
          </h2>
          <p className="text-xl text-primary-200 mb-8">
            Join thousands of families who are already preserving their precious memories 
            and valuable items on LegacyLayer.
          </p>
          <Link
            to="/mint"
            className="inline-flex items-center gap-2 bg-accent-wood hover:bg-accent-wood-light text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
          >
            <Sparkles className="w-5 h-5" />
            Start Your Journey
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePageDetailed; 